import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom/dist";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import {
  ROLL_CALL_EMPLOYEES_LIST,
  ROLL_CALL_API_BASE_URL,
} from "../RollCallConfig/RollCallConfig";
import { Select, Spin } from "antd";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import { RotateCcw } from "feather-icons-react/build/IconComponents";

const EmployeeShiftHistory = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empShiftsData, setEmpShiftsData] = useState([]);
  const [empInfo, setEmpInfo] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });

  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;
    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (perms) {
      setPerm({
        Read: perms.canRead,
        Write: perms.canWrite,
        Delete: perms.canDelete,
        Export: perms.canExport,
      });
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  // Fetch employee list for dropdown
  useEffect(() => {
    if (!tokenData || !superIdData) return;
    setEmpLoading(true);
    fetch(
      `${ROLL_CALL_API_BASE_URL}${ROLL_CALL_EMPLOYEES_LIST}?superid=${superIdData}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${tokenData}` },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.status) setEmployees(data.ResultData || []);
      })
      .catch(console.error)
      .finally(() => setEmpLoading(false));
  }, [tokenData, superIdData]);

  // Fetch shift history when employee selected
  useEffect(() => {
    if (!selectedEmp || !tokenData) return;
    setDataLoading(true);
    setEmpInfo(null);
    setEmpShiftsData([]);

    fetch(
      `${ROLL_CALL_API_BASE_URL}portal/shifts/getAssignshiftByEmp?regid=${selectedEmp}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${tokenData}` },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.status && data.ResultData?.length > 0) {
          const result = data.ResultData;
          setEmpShiftsData(result);
          setEmpInfo({
            badge: result[0]?.Badge || "N/A",
            department: result[0]?.deptname || "N/A",
            designation: result[0]?.Designation || "N/A",
          });
        } else {
          setEmpShiftsData([]);
          setEmpInfo(null);
        }
      })
      .catch(console.error)
      .finally(() => setDataLoading(false));
  }, [selectedEmp, tokenData]);

  const empOptions = useMemo(() => {
    const map = new Map();
    employees.forEach((emp) => {
      if (!map.has(emp.RegId)) {
        map.set(emp.RegId, { value: emp.RegId, label: emp.UserName });
      }
    });
    return Array.from(map.values());
  }, [employees]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(dateStr));
  };

  const handleDownloadExcel = () => {
    if (!empShiftsData || empShiftsData.length === 0) return;
    const empName =
      empOptions.find((e) => e.value === selectedEmp)?.label || "Employee";
    const wb = XLSX.utils.book_new();
    const wsData = empShiftsData.map((item, index) => ({
      "S.No": index + 1,
      Shift: item.Name || "N/A",
      "Start Date": formatDate(item.StartDate),
      "End Date": formatDate(item.EndDate),
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Shift History");
    XLSX.writeFile(wb, `ShiftHistory_${empName}.xlsx`);
  };

  const generatePDF = () => {
    if (!empShiftsData || empShiftsData.length === 0) return;
    const empName =
      empOptions.find((e) => e.value === selectedEmp)?.label || "Employee";
    const doc = new jsPDF();
    const generatedDate = new Date().toLocaleString();
    const tableColumn = ["S.No", "Shift", "Start Date", "End Date"];
    const tableRows = empShiftsData.map((item, index) => [
      index + 1,
      item.Name || "N/A",
      formatDate(item.StartDate),
      formatDate(item.EndDate),
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      didDrawPage: function () {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(12);
        doc.text(`Shift History — ${empName}`, 14, 10);
        if (empInfo) {
          doc.setFontSize(9);
          doc.text(
            `Badge: ${empInfo.badge}   |   Dept: ${empInfo.department}   |   Designation: ${empInfo.designation}`,
            14,
            17,
          );
        }
        doc.setFontSize(9);
        doc.text(`Generated: ${generatedDate}`, pageWidth - 14, 10, {
          align: "right",
        });
        doc.text(
          "RollCall Attendance System. Powered by Perennial Code IT Consultants",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" },
        );
      },
    });
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, {
        align: "right",
      });
    }
    doc.save(`ShiftHistory_${empName}.pdf`);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const columns = [
   
    {
      title: "Shift",
      dataIndex: "Name",
      sorter: (a, b) => (a.Name || "").localeCompare(b.Name || ""),
      render: (text) => text || "N/A",
    },
    {
      title: "Start Date",
      dataIndex: "StartDate",
      sorter: (a, b) => new Date(a.StartDate) - new Date(b.StartDate),
      render: (text) => formatDate(text),
    },
    {
      title: "End Date",
      dataIndex: "EndDate",
      sorter: (a, b) => new Date(a.EndDate) - new Date(b.EndDate),
      render: (text) => formatDate(text),
    },
  ];

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/rollcall/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item" aria-current="page">
                      Employee Shift History
                    </h4>
                  </ol>
                </nav>
                <h6>View shift history for an employee</h6>
              </div>
            </div>

            <ul className="table-top-head">
              {perm?.Export && (
                <>
                  <li>
                    <OverlayTrigger placement="top" overlay={renderTooltip}>
                      <Link onClick={generatePDF}>
                        <ImageWithBasePath
                          src="assets/img/icons/pdf.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                  <li>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderExcelTooltip}
                    >
                      <Link onClick={handleDownloadExcel}>
                        <ImageWithBasePath
                          src="assets/img/icons/excel.svg"
                          alt="img"
                        />
                      </Link>
                    </OverlayTrigger>
                  </li>
                </>
              )}
              <li onClick={handleReload}>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>

            {perm?.Write && (
              <div className="page-btn mb-3">
                <Link
                  to="/rollcall/shifts"
                  className="btn btn-success btn-sm"
                  title="Assign Shift"
                >
                  <i className="ti ti-circle-plus fs-4"></i>
                </Link>
              </div>
            )}
          </div>

          {perm.Read ? (
            <div className="card table-list-card no-search">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div><div className="search-set"></div>
                  <div className="d-flex align-items-center flex-wrap row-gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0 fw-semibold text-nowrap">
                        
                      </label>
                      {empLoading ? (
                        <Spin size="small" />
                      ) : (
                        <Select
                          placeholder="Search employee..."
                          value={selectedEmp}
                          onChange={setSelectedEmp}
                          options={empOptions}
                          showSearch
                          optionFilterProp="label"
                          style={{ minWidth: 220 }}
                        />
                      )}
                    </div>

                    {empInfo && (
                      <div className="d-flex align-items-center gap-2 ms-3 flex-wrap">
                        <span className="badge bg-primary-subtle text-primary px-3 py-2">
                          <i className="fas fa-id-badge me-1" />
                          {empInfo.badge}
                        </span>
                        <span className="badge bg-info-subtle text-info px-3 py-2">
                          <i className="fas fa-building me-1" />
                          {empInfo.department}
                        </span>
                        <span className="badge bg-secondary-subtle text-secondary px-3 py-2">
                          <i className="fas fa-briefcase me-1" />
                          {empInfo.designation}
                        </span>
                        <span className="badge bg-success-subtle text-success px-3 py-2">
                          <i className="fas fa-history me-1" />
                          {empShiftsData.length} Record
                          {empShiftsData.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive brand-table">
                  {dataLoading ? (
                    <div className="text-center my-4">
                      <Spin size="large" />
                      <p>Loading data...</p>
                    </div>
                  ) : !selectedEmp ? (
                    <div className="text-center my-5 text-muted">
                      <i className="fas fa-user-clock fa-3x mb-3 d-block" />
                      <p>Select an employee to view their shift history.</p>
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={empShiftsData}
                      pgno={true}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body d-flex justify-content-center">
                <h1>You don&apos;t have access to this page</h1>
              </div>
            </div>
          )}
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default EmployeeShiftHistory;
